import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Trash, ArrowCounterClockwise, MagnifyingGlass, CalendarX, Phone } from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';

const CATEGORY_STYLES = {
  'New': 'bg-blue-100 text-blue-800 border-blue-300',
  '10 Days': 'bg-green-100 text-green-800 border-green-300',
  '30 Days': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  '60 Days': 'bg-orange-100 text-orange-800 border-orange-300',
  '90 Days': 'bg-red-100 text-red-800 border-red-300',
  '120 Days': 'bg-purple-100 text-purple-800 border-purple-300',
  'Buyers': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Challenge Therapy': 'bg-pink-100 text-pink-800 border-pink-300',
};

const DeletedCustomers = () => {
  const { activeBranch } = useBranch();
  const [data, setData] = useState({ groups: [], total: 0, dormant_days: 20 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [restoring, setRestoring] = useState(null);

  const fetchDormant = useCallback(async () => {
    if (!activeBranch?.branch_id) return;
    try {
      setLoading(true);
      const res = await api.get('/customers/dormant', { params: { branch_id: activeBranch.branch_id } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load deleted customers');
    } finally {
      setLoading(false);
    }
  }, [activeBranch]);

  useEffect(() => { fetchDormant(); }, [fetchDormant]);

  const handleRestore = async (cust) => {
    try {
      setRestoring(cust.customer_id);
      const res = await api.post(`/customers/${cust.customer_id}/restore`);
      toast.success(`${cust.name} restored to the main list (Reg No ${res.data.reg_no})`);
      setData((prev) => {
        const groups = prev.groups
          .map((g) => ({ ...g, customers: g.customers.filter((c) => c.customer_id !== cust.customer_id) }))
          .map((g) => ({ ...g, count: g.customers.length }))
          .filter((g) => g.count > 0);
        return { ...prev, groups, total: prev.total - 1 };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore customer');
    } finally {
      setRestoring(null);
    }
  };

  const q = search.trim().toLowerCase();
  const visibleGroups = data.groups
    .map((g) => ({
      ...g,
      customers: q ? g.customers.filter((c) => c.name?.toLowerCase().includes(q) || String(c.reg_no).includes(q) || c.phone_number?.includes(q)) : g.customers,
    }))
    .filter((g) => g.customers.length > 0);

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="deleted-customers-page">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#2C4C3B] flex items-center gap-3">
              <Trash size={36} weight="fill" className="text-[#C05C42]" />
              Deleted
            </h1>
            <p className="text-sm text-[#1C1C1A]/60 mt-1">
              Customers in <span className="font-medium">{activeBranch?.name || 'this branch'}</span> with no check-in for {data.dormant_days} consecutive days.
              Any check-in or Restore brings them back under their original Registration Number.
            </p>
          </div>
          <div className="relative w-full lg:w-72">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1C1C1A]/40" />
            <Input data-testid="deleted-search-input" placeholder="Search name, reg no or phone…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 border-[#E5E5DF]" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200" data-testid="deleted-total-card">
            <p className="text-xs font-medium text-rose-800">Total in Deleted</p>
            <p className="text-2xl font-bold text-rose-900 mt-1">{data.total}</p>
          </Card>
          {data.groups.slice(0, 3).map((g) => (
            <Card key={g.category} className="p-4 bg-white border-[#E5E5DF]">
              <p className="text-xs font-medium text-[#1C1C1A]/60">{g.category}</p>
              <p className="text-2xl font-bold text-[#2C4C3B] mt-1">{g.count}</p>
            </Card>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#2C4C3B]"></div>
          </div>
        ) : visibleGroups.length === 0 ? (
          <Card className="p-12 text-center bg-white border-[#E5E5DF]" data-testid="deleted-empty-state">
            <CalendarX size={56} weight="thin" className="mx-auto text-[#1C1C1A]/20 mb-3" />
            <p className="text-lg text-[#1C1C1A]/60">{q ? 'No matches in Deleted' : 'No deleted customers'}</p>
            <p className="text-sm text-[#1C1C1A]/40 mt-1">Customers appear here automatically after {data.dormant_days} days without a check-in.</p>
          </Card>
        ) : (
          visibleGroups.map((group) => (
            <Card key={group.category} className="p-0 bg-white border-[#E5E5DF] overflow-hidden" data-testid={`deleted-group-${group.category.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="flex items-center justify-between px-5 py-3 bg-[#F9F9F8] border-b border-[#E5E5DF]">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${CATEGORY_STYLES[group.category] || 'bg-gray-100 text-gray-800 border-gray-300'}`}>{group.category}</span>
                  <span className="text-sm text-[#1C1C1A]/60">{group.customers.length} {group.customers.length === 1 ? 'customer' : 'customers'}</span>
                </div>
              </div>
              <ul className="divide-y divide-[#E5E5DF]">
                {group.customers.map((c) => (
                  <li key={c.customer_id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3 hover:bg-[#F9F9F8]" data-testid={`deleted-customer-${c.customer_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#2C4C3B] truncate">{c.name}</p>
                      <p className="text-xs text-[#1C1C1A]/60 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                        <span>Reg No <span className="font-medium text-[#1C1C1A]">{c.reg_no}</span></span>
                        <span className="inline-flex items-center gap-1"><Phone size={12} /> {c.phone_number}</span>
                        <span>{c.sessions_completed} sessions</span>
                      </p>
                    </div>
                    <div className="text-xs text-[#1C1C1A]/70 sm:text-right">
                      <p>Last check-in: <span className="font-medium text-[#1C1C1A]">{c.last_checkin_date || 'Never'}</span></p>
                      <p className="text-[#C05C42] font-medium">{c.days_absent != null ? `${c.days_absent} days absent` : ''}</p>
                    </div>
                    <Button size="sm" data-testid={`restore-btn-${c.customer_id}`} disabled={restoring === c.customer_id} onClick={() => handleRestore(c)}
                      className="bg-[#2C4C3B] hover:bg-[#1F3529] text-white sm:ml-2">
                      <ArrowCounterClockwise size={16} className="mr-1.5" />
                      {restoring === c.customer_id ? 'Restoring…' : 'Restore'}
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
          ))
        )}
      </div>
    </Layout>
  );
};

export default DeletedCustomers;
